//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import BlindFeedSettingsModel from './blindFeedSettings.model';

/**
 * @service BlindFeedSettingsService
 * @description Service for managing blind feed settings configuration and species-type associations.
 * Provides HTTP methods for retrieving blind feed settings with their related species type mappings.
 * Blind feed refers to automated feeding schedules without real-time monitoring feedback.
 * Extends base Service class with generic type BlindFeedSettingsModel.
 *
 * @feature 1. Query all blind feed settings with associated species types
 * @feature 2. Retrieve species type relationships for blind feed configurations
 * @feature 3. Support feeding schedule management by aquaculture species
 * @feature 4. Cancel ongoing HTTP requests to prevent memory leaks
 *
 * @architecture
 * Extends base Service<BlindFeedSettingsModel> providing REST API integration with
 * aquaculture backend. Handles blind feed setting configurations that define automated
 * feeding parameters (quantity, frequency, timing) for specific species types. Implements
 * request tracking for cleanup and cancellation.
 *
 * @service-endpoint /aqu/blindfeedsettings/
 * @service-provider root (singleton, application-wide)
 * @service-type Aquaculture Configuration Service
 *
 * @domain Aquaculture (AQU) - Blind Feed Settings Management
 * @author AquaCloud Development Team
 * @version 1.0.0
 * @created 2025-01-19
 */
@Injectable({
  providedIn: 'root'
})
export class BlindFeedSettingsService extends Service<BlindFeedSettingsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes BlindFeedSettingsService by calling parent Service constructor with
   * HttpClient instance and service endpoint path (/aqu/blindfeedsettings/). Sets up
   * base URL configuration and HTTP options for blind feed settings operations.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   *   Used for querying blind feed settings and species type associations
   *
   * @returns {void}
   *
   * @execution-flow
   * 1. Receive HttpClient dependency injection from Angular injector
   * 2. Call super() with httpClient and service endpoint path
   * 3. Parent Service class configures baseUrl and httpOptions
   * 4. Service ready for blind feed settings queries on /aqu/blindfeedsettings/ endpoint
   *
   * @example
   * // Service automatically provided via dependency injection
   * constructor(private bfsService: BlindFeedSettingsService) {
   *   // Service initialized and ready for use
   * }
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/blindfeedsettings/');
  }

  /**
   * @method getAllBFWithSpeciesType
   * @async
   * @description Fetch all blind feed settings with their associated species types
   *
   * Makes HTTP GET request to backend endpoint to retrieve complete list of blind feed
   * settings configurations including mapped species type relationships. Returns array
   * of BlindFeedSettingsModel objects enriched with species type information.
   *
   * @returns {Promise<Array<BlindFeedSettingsModel>>} Promise resolving to array of blind feed settings with species types
   *
   * @endpoint GET /aqu/blindfeedsettings/findAllblindFeedSettings/withSpeciesTypes
   *
   * @response-data {Array<BlindFeedSettingsModel>}
   * Each item contains:
   * - id: Blind feed setting ID
   * - setting_name: Name of the blind feed configuration
   * - feed_rate: Feeding rate (quantity per time unit)
   * - feed_frequency: Frequency of feeding (times per day/hour)
   * - species_type_id: Associated species type ID
   * - species_type_name: Associated species type name
   * - start_time: Feeding start time (HH:MM format)
   * - end_time: Feeding end time (HH:MM format)
   * - is_active: Boolean flag for active status
   *
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const bfSettings = await this.bfsService.getAllBFWithSpeciesType();
   * console.log(bfSettings); // Array of blind feed settings with species types
   * bfSettings.forEach(setting => {
   *   console.log(`${setting.setting_name} for ${setting.species_type_name}`);
   * });
   */
  public getAllBFWithSpeciesType() {
    return new Promise<Array<BlindFeedSettingsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BlindFeedSettingsModel>>(`${this.baseUrl}${this.serviceUrl}findAllblindFeedSettings/withSpeciesTypes`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   * - Prevents completion callbacks for cancelled requests
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   * @example
   * ngOnDestroy() {
   *   this.bfsService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
